import {
  VideoAspectRatio,
  type VideoGenerationMode,
  VideoGenerationModel,
  VideoResolution,
} from '@/ai/video-models';
import type { GenerationContinuationType } from '@/db/app.schema';
import {
  VIDEO_REFERENCE_MODES,
  VIDEO_REFERENCE_TEMPLATES,
  normalizeVideoReferenceRoles,
  serializeVideoReferenceRoles,
  type VideoReferenceMode,
  type VideoReferenceTemplate,
} from '@/lib/video-reference-roles';

export interface VideoRouteSearch {
  prompt?: string;
  model?: VideoGenerationModel;
  aspectRatio?: VideoAspectRatio;
  duration?: number;
  resolution?: VideoResolution;
  mode?: VideoGenerationMode;
  generateAudio?: '1';
  referenceToken?: string;
  referenceMode?: VideoReferenceMode;
  referenceTemplate?: VideoReferenceTemplate;
  referenceRoles?: string;
  parentGenerationId?: string;
  continuationType?: GenerationContinuationType;
}

const VIDEO_MODELS = new Set<string>(Object.values(VideoGenerationModel));

const VIDEO_ASPECT_RATIOS = new Set<string>(Object.values(VideoAspectRatio));

const VIDEO_RESOLUTIONS = new Set<string>(Object.values(VideoResolution));

const VIDEO_MODES = new Set<string>(['normal', 'fun', 'spicy']);
const VIDEO_REFERENCE_MODE_VALUES = new Set<string>(VIDEO_REFERENCE_MODES);
const VIDEO_REFERENCE_TEMPLATE_VALUES = new Set<string>(
  VIDEO_REFERENCE_TEMPLATES
);

export function validateVideoSearch(
  search: Record<string, unknown>
): VideoRouteSearch {
  const result: VideoRouteSearch = {};
  const prompt = toOptionalString(search.prompt);
  const model = toEnum(search.model, VIDEO_MODELS) as VideoGenerationModel;
  const aspectRatio = toEnum(
    search.aspectRatio,
    VIDEO_ASPECT_RATIOS
  ) as VideoAspectRatio;
  const duration = toPositiveInteger(search.duration);
  const resolution = toEnum(
    search.resolution,
    VIDEO_RESOLUTIONS
  ) as VideoResolution;
  const mode = toEnum(search.mode, VIDEO_MODES) as VideoGenerationMode;
  const referenceToken = toOptionalString(search.referenceToken);
  const referenceMode = toEnum(
    search.referenceMode,
    VIDEO_REFERENCE_MODE_VALUES
  ) as VideoReferenceMode;
  const referenceTemplate = toEnum(
    search.referenceTemplate,
    VIDEO_REFERENCE_TEMPLATE_VALUES
  ) as VideoReferenceTemplate;
  const referenceRoles = normalizeVideoReferenceRoles(search.referenceRoles);
  const parentGenerationId = toOptionalString(search.parentGenerationId);
  const continuationType = toContinuationType(search.continuationType);

  if (prompt) result.prompt = prompt;
  if (model) result.model = model;
  if (aspectRatio) result.aspectRatio = aspectRatio;
  if (duration) result.duration = duration;
  if (resolution) result.resolution = resolution;
  if (mode) result.mode = mode;
  if (search.generateAudio === '1') result.generateAudio = '1';
  if (referenceToken) result.referenceToken = referenceToken;
  if (referenceMode) result.referenceMode = referenceMode;
  if (referenceTemplate) result.referenceTemplate = referenceTemplate;
  if (referenceRoles.length > 0) {
    result.referenceRoles = serializeVideoReferenceRoles(referenceRoles);
  }
  if (parentGenerationId && continuationType) {
    result.parentGenerationId = parentGenerationId;
    result.continuationType = continuationType;
  }

  return result;
}

function toContinuationType(value: unknown) {
  return value === 'continue' || value === 'variation' || value === 'retry'
    ? value
    : undefined;
}

function toOptionalString(value: unknown) {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function toPositiveInteger(value: unknown) {
  if (value === undefined || value === null || value === '') return undefined;
  const numberValue = Number(value);
  if (!Number.isInteger(numberValue) || numberValue <= 0) return undefined;
  return numberValue;
}

function toEnum(value: unknown, allowed: Set<string>) {
  return typeof value === 'string' && allowed.has(value) ? value : undefined;
}
